import Phaser from 'phaser'
import { COLORS, PEARL_COLORS, PEARL_RADIUS } from '../config'

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' })
  }

  preload() {
    // Create loading bar
    const width = this.cameras.main.width
    const height = this.cameras.main.height
    
    const progressBar = this.add.graphics()
    const progressBox = this.add.graphics()
    progressBox.fillStyle(COLORS.mediumTeal, 0.3)
    progressBox.fillRoundedRect(width / 2 - 160, height / 2 - 15, 320, 30, 10)
    
    const loadingText = this.add.text(width / 2, height / 2 - 50, 'Loading...', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '24px',
      color: '#B8F0E8'
    }).setOrigin(0.5)

    // Update progress bar
    this.load.on('progress', (value: number) => {
      progressBar.clear()
      progressBar.fillStyle(COLORS.lightTeal, 1)
      progressBar.fillRoundedRect(width / 2 - 155, height / 2 - 10, 310 * value, 20, 8)
    })

    this.load.on('complete', () => {
      progressBar.destroy()
      progressBox.destroy()
      loadingText.destroy()
    })

    // Load the Thea mermaid character image
    this.load.image('thea', '/thea-mermaid-logo-transparent.png')
    
    // Generate all game assets programmatically
    this.createGameAssets()
  }

  createGameAssets() {
    // Create pearl sprites for each color
    PEARL_COLORS.forEach((color, index) => {
      this.createPearlSprite(color.name, color.hex, index)
    })
    
    // Create slingshot base sprite
    this.createSlingshotSprite()
    
    // Create bubble sprite for effects
    this.createBubbleSprite()
    
    // Create trajectory dot
    this.createTrajectoryDot()
    
    // Create sparkle particle
    this.createSparkleSprite()
  }

  createPearlSprite(name: string, color: number, index: number) {
    const graphics = this.make.graphics({ x: 0, y: 0 })
    const size = PEARL_RADIUS * 2
    const center = PEARL_RADIUS
    
    // Main pearl body with gradient effect
    graphics.fillStyle(color, 1)
    graphics.fillCircle(center, center, PEARL_RADIUS - 2)
    
    // Outer glow/rim
    graphics.lineStyle(2, color, 0.8)
    graphics.strokeCircle(center, center, PEARL_RADIUS - 1)
    
    // Inner highlight (top-left)
    graphics.fillStyle(COLORS.white, 0.6)
    graphics.fillCircle(center - 6, center - 6, 5)
    
    // Small secondary highlight
    graphics.fillStyle(COLORS.white, 0.3)
    graphics.fillCircle(center - 3, center - 9, 2)
    
    // Subtle pattern/texture (different per pearl for accessibility)
    graphics.lineStyle(1, COLORS.white, 0.15)
    switch (index % 3) {
      case 0: // Horizontal lines
        graphics.beginPath()
        graphics.moveTo(center - 8, center + 4)
        graphics.lineTo(center + 8, center + 4)
        graphics.strokePath()
        break
      case 1: // Dots
        graphics.fillStyle(COLORS.white, 0.2)
        graphics.fillCircle(center + 4, center + 4, 2)
        break
      case 2: // Cross
        graphics.beginPath()
        graphics.moveTo(center + 2, center + 2)
        graphics.lineTo(center + 8, center + 8)
        graphics.moveTo(center + 8, center + 2)
        graphics.lineTo(center + 2, center + 8)
        graphics.strokePath()
        break
    }
    
    graphics.generateTexture(`pearl_${name}`, size, size)
    graphics.destroy()
  }

  createSlingshotSprite() {
    const graphics = this.make.graphics({ x: 0, y: 0 })
    
    // Slingshot base - coral colored wooden Y shape
    graphics.fillStyle(COLORS.sandyCoral)
    
    // Left arm
    graphics.fillRoundedRect(10, 0, 12, 50, 4)
    // Right arm
    graphics.fillRoundedRect(58, 0, 12, 50, 4)
    // Handle
    graphics.fillRoundedRect(30, 35, 20, 45, 6)
    
    // Wood grain details
    graphics.lineStyle(1, COLORS.brightCoral, 0.5)
    graphics.beginPath()
    graphics.moveTo(35, 40)
    graphics.lineTo(35, 75)
    graphics.moveTo(45, 42)
    graphics.lineTo(45, 78)
    graphics.strokePath()
    
    // Elastic band attachment points
    graphics.fillStyle(COLORS.deepPurple)
    graphics.fillCircle(16, 5, 4)
    graphics.fillCircle(64, 5, 4)
    
    graphics.generateTexture('slingshot', 80, 80)
    graphics.destroy()
  }

  createBubbleSprite() {
    const graphics = this.make.graphics({ x: 0, y: 0 })
    
    // Bubble - 12x12
    graphics.lineStyle(2, COLORS.iceAqua, 0.8)
    graphics.strokeCircle(6, 6, 5)
    
    // Highlight
    graphics.fillStyle(COLORS.white, 0.6)
    graphics.fillCircle(4, 4, 2)
    
    graphics.generateTexture('bubble', 12, 12)
    graphics.destroy()
  }

  createTrajectoryDot() {
    const graphics = this.make.graphics({ x: 0, y: 0 })
    
    graphics.fillStyle(COLORS.iceAqua, 0.7)
    graphics.fillCircle(4, 4, 4)
    
    graphics.generateTexture('trajectoryDot', 8, 8)
    graphics.destroy()
  }

  createSparkleSprite() {
    const graphics = this.make.graphics({ x: 0, y: 0 })
    
    // Simple sparkle/star shape
    graphics.fillStyle(COLORS.white, 0.9)
    
    // 4-point star
    graphics.beginPath()
    graphics.moveTo(8, 0)
    graphics.lineTo(10, 6)
    graphics.lineTo(16, 8)
    graphics.lineTo(10, 10)
    graphics.lineTo(8, 16)
    graphics.lineTo(6, 10)
    graphics.lineTo(0, 8)
    graphics.lineTo(6, 6)
    graphics.closePath()
    graphics.fillPath()
    
    graphics.generateTexture('sparkle', 16, 16)
    graphics.destroy()
  }

  create() {
    this.scene.start('MenuScene')
  }
}
